import React, { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, CheckCircle2 } from "lucide-react";
import useSeo from "../hooks/useSeo";
import { SEO_PAGES, SERVICE_LINKS, CITY_LINKS } from "../seoContent";
import { wa } from "../mock";
import BrandLockup from "./BrandLockup";
import Footer from "./Footer";
import { InquiryForm } from "./InquiryForm";
import NotFound from "./NotFound";
import { ContactLink } from "./ContactLink";

function useJsonLd(id, data) {
  useEffect(() => {
    if (!data) return;
    let el = document.getElementById(id);
    if (!el) {
      el = document.createElement("script");
      el.type = "application/ld+json";
      el.id = id;
      document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(data);
    return () => { el && el.remove(); };
  }, [id, data]);
}

export default function SeoPage() {
  const { slug } = useParams();
  const page = SEO_PAGES[slug];
  useSeo({ title: page?.title, description: page?.description });

  const origin = typeof window !== "undefined" ? window.location.origin : "";
  useJsonLd("ld-breadcrumb", page && {
    "@context": "https://schema.org", "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: `${origin}/` },
      { "@type": "ListItem", position: 2, name: page.h1, item: `${origin}/${slug}` },
    ],
  });
  useJsonLd("ld-service", page && {
    "@context": "https://schema.org", "@type": "Service",
    name: page.h1, description: page.description,
    provider: { "@type": "Organization", name: "Kapatia Pvt Ltd", url: origin, telephone: "+91 92173 99766" },
    areaServed: ["Noida", "Delhi", "Delhi NCR", "Gurugram", "Ghaziabad", "Faridabad", "Bangalore"],
  });
  useJsonLd("ld-faq", page?.faqs?.length ? {
    "@context": "https://schema.org", "@type": "FAQPage",
    mainEntity: page.faqs.map((f) => ({
      "@type": "Question", name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  } : null);

  useEffect(() => { window.scrollTo(0, 0); }, [slug]);

  if (!page) return <NotFound />;

  const related = [...SERVICE_LINKS, ...CITY_LINKS].filter((l) => l.href !== `/${slug}`).slice(0, 7);

  return (
    <div className="public-site min-h-screen" style={{ background: "var(--cream-2)" }}>
      <header className="border-b" style={{ background: "var(--cream)", borderColor: "var(--line)" }}>
        <div className="container flex items-center justify-between py-3">
          <a href="/" data-testid="seo-home-link" aria-label="Kapatia Pvt Ltd home"><BrandLockup nameSize="text-[19px]" hindiSize="text-[18px]" citySize="text-[9px]" /></a>
          <div className="flex items-center gap-2.5">
            <a href={wa(`Hi Kapatia, I have an enquiry about ${page.h1}.`)} target="_blank" rel="noopener noreferrer"
               data-testid="seo-whatsapp-cta"
               className="kp-button kp-button--compact inline-flex items-center gap-2 rounded-full px-4 py-2 font-heading text-[13px] font-bold text-white"
               style={{ background: "linear-gradient(135deg, var(--green-800), var(--green-950))" }}>
              <img src="/assets/icons/whatsapp-clean-square.png" alt="" className="h-4 w-4" /> WhatsApp
            </a>
            <ContactLink testId="seo-header-phone" variant="button"
              className="kp-button kp-button--compact hidden rounded-full border sm:inline-flex"
              style={{ borderColor: "var(--line)" }} />
          </div>
        </div>
      </header>

      <main className="container max-w-4xl py-10" data-testid="seo-page">
        <nav aria-label="Breadcrumb" className="text-[13px]" style={{ color: "var(--muted)" }}>
          <Link to="/" data-testid="seo-breadcrumb-home" className="inline-flex items-center gap-1 font-semibold" style={{ color: "var(--green-800)" }}>
            <ArrowLeft size={13} /> Home
          </Link>
          <span className="mx-2">/</span><span>{page.eyebrow}</span>
        </nav>

        <p className="eyebrow mt-6">{page.eyebrow}</p>
        <h1 data-testid="seo-heading" className="mt-3 font-heading font-semibold leading-[1.05]"
            style={{ color: "var(--green-950)", fontSize: "clamp(30px, 4.4vw, 46px)" }}>
          {page.h1}
        </h1>
        {page.intro.map((p, i) => (
          <p key={i} className="mt-4 max-w-[720px] text-[16.5px] leading-relaxed" style={{ color: "#31463a" }}>{p}</p>
        ))}

        <div className="mt-8 grid gap-5">
          {page.sections.map((s, i) => (
            <section key={s.heading} className="rounded-2xl border p-6" style={{ background: "#fff", borderColor: "var(--line)" }}>
              <h2 data-testid={`seo-section-heading-${i}`} className="font-heading text-lg font-semibold" style={{ color: "var(--green-950)" }}>{s.heading}</h2>
              {s.body && <p className="mt-2.5 text-[15px] leading-relaxed" style={{ color: "#31463a" }}>{s.body}</p>}
              {s.bullets && (
                <ul className="mt-3 grid gap-2">
                  {s.bullets.map((b) => (
                    <li key={b} className="flex items-start gap-2.5 text-[15px]" style={{ color: "#31463a" }}>
                      <CheckCircle2 size={16} className="mt-0.5 shrink-0" style={{ color: "#E8501A" }} /> {b}
                    </li>
                  ))}
                </ul>
              )}
            </section>
          ))}
        </div>

        {page.faqs?.length > 0 && (
          <section className="mt-8">
            <h2 data-testid="seo-faq-heading" className="font-heading text-xl font-semibold" style={{ color: "var(--green-950)" }}>Frequently Asked Questions</h2>
            <div className="mt-4 grid gap-3">
              {page.faqs.map((f, i) => (
                <details key={f.q} className="rounded-xl border bg-white px-5 py-4" style={{ borderColor: "var(--line)" }}>
                  <summary data-testid={`seo-faq-${i}`} className="cursor-pointer font-heading text-[15px] font-medium" style={{ color: "var(--green-900)" }}>{f.q}</summary>
                  <p className="mt-2 text-[14.5px] leading-relaxed" style={{ color: "#31463a" }}>{f.a}</p>
                </details>
              ))}
            </div>
          </section>
        )}

        <section className="mt-10 rounded-2xl p-7 text-center" style={{ background: "linear-gradient(135deg, var(--green-800), var(--green-950))" }}>
          <h2 data-testid="seo-quotation-heading" className="font-heading text-xl font-semibold text-white">Get a Quotation Today</h2>
          <p className="mx-auto mt-2 max-w-[520px] text-[14.5px]" style={{ color: "rgba(255,255,255,0.85)" }}>
            Share your requirement or asset list — we respond quickly with a practical quote and next steps.
          </p>
          <div className="mt-5 flex flex-wrap justify-center gap-3">
            {page.inventory && (
              <Link to={page.inventory.href} data-testid="seo-cta-browse-stock"
                    className="kp-button inline-flex items-center gap-2 rounded-full bg-white px-6 py-3 font-heading text-[14px] font-bold" style={{ color: "var(--green-900)" }}>
                {page.inventory.label}
              </Link>
            )}
            <a href={wa(`Hi Kapatia, I have an enquiry about ${page.h1}.`)} target="_blank" rel="noopener noreferrer" data-testid="seo-quotation-whatsapp"
               className="kp-button inline-flex items-center gap-2 rounded-full bg-white px-6 py-3 font-heading text-[14px] font-bold" style={{ color: "var(--green-900)" }}>
              <img src="/assets/icons/whatsapp-clean-square.png" alt="" className="h-5 w-5" /> WhatsApp Us
            </a>
            <ContactLink testId="seo-quotation-phone" variant="button" inverse
              className="kp-button rounded-full border"
              style={{ borderColor: "rgba(255,255,255,0.4)" }} />
            <a href="/#contact" data-testid="seo-quotation-enquiry" className="kp-button inline-flex items-center rounded-full border px-6 py-3 font-heading text-[14px] font-bold text-white" style={{ borderColor: "rgba(255,255,255,0.4)" }}>
              Enquiry Form
            </a>
          </div>
        </section>

        <section className="mt-8" id="enquiry">
          <InquiryForm campaign={slug} heading={`Enquire about ${page.eyebrow}`}
                       requirementPlaceholder="Item, quantity, condition and your city" />
        </section>

        <section className="mt-10">
          <h2 data-testid="seo-related-heading" className="font-heading text-[15px] font-semibold uppercase" style={{ color: "var(--green-950)" }}>Related Services & Areas</h2>
          <div className="mt-3 flex flex-wrap gap-2">
            {related.map((l) => (
              <Link key={l.href} to={l.href} data-testid={`seo-related-${l.href.slice(1)}`} className="kp-button kp-button--compact inline-flex rounded-full border bg-white px-4 py-2 font-heading text-[13px] font-bold transition-colors hover:text-white"
                    style={{ borderColor: "var(--line)", color: "var(--green-900)" }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = "var(--green-900)"; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = "#fff"; }}>
                {l.label}
              </Link>
            ))}
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
